import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Chip,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Divider,
  addToast,
} from '@heroui/react';
import { DatePicker } from '@heroui/date-picker';
import { getUser } from 'src/utils/user/user';
import { useGetVehiclesListQuery } from 'src/services/transporterVehicles/transporterVehicles';
import { Employee, VehicleFirst } from 'src/utils/interfaces/interfaces';
import {
  useGetDriverListQuery,
  useGetActiveEmployeeListQuery,
  useGetEscortListQuery,
} from 'src/services/transporterDrivers/transporterDrivers';
import {
  useCreateOrderMutation,
  useUpdateOrderMutation,
} from 'src/services/orders/orders';
import {
  getLocalTimeZone,
  ZonedDateTime,
  today,
} from '@internationalized/date';
import {
  GoogleMap,
  useJsApiLoader,
  Autocomplete,
  Marker,
} from '@react-google-maps/api';
import { CONSTANTS } from 'src/constants/constants';
import { t } from 'i18next';
import CustomSnackbar from 'src/components/UI/Alerts/customSnackbar';
import FileUpload from 'src/components/UI/Forms/Inputs/fileUpload';
import i18n from 'src/i18n';
import { FaPlus, FaXmark } from 'src/icons';
import { Input } from 'src/components/UI/Forms/Inputs';

export interface CreateOrderProps {
  onClose?: () => void;
  onOrderCreated?: () => void;
}

interface ShipmentDetail {
  itemNo: string;
  description: string;
  hd: string;
  unSNo: string;
  boxesQty: string;
  netWt: string;
  grossWt: string;
  maxSize: string;
  maxHt: string;
}

interface ShipmentGroup {
  shipmentDetails: ShipmentDetail[];
}

const EMPTY_DETAIL: ShipmentDetail = {
  itemNo: '',
  description: '',
  hd: '',
  unSNo: '',
  boxesQty: '',
  netWt: '',
  grossWt: '',
  maxSize: '',
  maxHt: '',
};

const stylesDateInput = {
  base: 'w-full',
  inputWrapper: [
    'bg-[var(--bg-surface)] shadow-sm border border-[var(--border-light)]',
    'text-[var(--text-main)]',
    'hover:!bg-[var(--bg-overlay)]',
    '!h-[48px]',
    '!min-h-[48px]',
    '!py-0',
    '!flex',
    '!items-center',
  ],
  segment: ['!text-[var(--text-main)]'],
  selectorButton: ['!text-[var(--text-muted)]'],
  innerWrapper: ['!items-center', '!flex'],
};

const shipmentInputClass =
  '!mb-0 border border-[var(--border-light)] bg-[var(--bg-surface)] text-[var(--text-main)] shadow-sm placeholder:text-[var(--text-muted)]';

const shipmentNumberInputClass =
  `${shipmentInputClass} bg-[var(--bg-surface-2)] font-medium`;

const formLabelClass = 'text-sm font-medium text-[var(--text-muted)]';

const formDividerClass = 'bg-[var(--border-light)]';

const secondaryActionButtonClass =
  'w-fit rounded-lg border border-[var(--border-light)] bg-[var(--bg-surface)] px-4 py-2 text-sm font-medium text-[var(--text-main)] shadow-sm transition-colors hover:bg-[var(--bg-overlay)]';

const fileUploadInputClass =
  'rounded-lg border border-dashed border-[var(--border-light)] bg-[var(--bg-surface)] !mb-2 !text-sm text-[var(--text-main)] shadow-sm placeholder:text-[var(--text-muted)]';

const defaultCenter = {
  lat: 25.2048, // Latitude
  lng: 55.2708, // Longitude
};

const libraries: any = ['places'];

export default function CreateOrderForm({
  onClose,
  onOrderCreated,
}: CreateOrderProps) {
  const user = getUser();
  const navigate = useNavigate();
  const isRtl = i18n.dir() === 'rtl';
  // Load the Google Maps JavaScript API + Places library
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: CONSTANTS.GOOGLE_JS_MAPS_KEY,
    libraries,
  });

  // States for map and places
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);

  const [selectedPlaceName, setSelectedPlaceName] = useState<string | null>(
    null
  );
  const [destination, setDestination] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // New states for origin selection
  const [originLocation, setOriginLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [selectedOriginName, setSelectedOriginName] = useState<string | null>(
    null
  );
  const [locationSelectionType, setLocationSelectionType] = useState<
    'origin' | 'destination'
  >('destination');

  // States for form fields
  const [selectedApplications, setSelectedApplications] =
    useState<string>('movement');
  const [selectedShipmentTypes, setSelectedShipmentTypes] =
    useState<string>('');
  const [trips, setTrips] = useState<number | null>(1);
  const [shipments, setShipments] = useState<ShipmentGroup[]>([
    { shipmentDetails: [EMPTY_DETAIL] },
  ]);

  const [validFrom, setValidFrom] = useState<ZonedDateTime | null>();
  const [validTo, setValidTo] = useState<ZonedDateTime | null>();
  const [eventDate, setEventDate] = useState<ZonedDateTime | null>();
  const [eventLetterFile, setEventLetterFile] = useState<File | null>(null);
  const [eventLayoutFile, setEventLayoutFile] = useState<File | null>(null);
  const [siraApprovalFile, setSiraApprovalFile] = useState<File | null>(null);
  const [isEventLetterFileValidating, setIsEventLetterFileValidating] = useState(false);
  const [isEventLayoutFileValidating, setIsEventLayoutFileValidating] = useState(false);
  const [isSiraApprovalFileValidating, setIsSiraApprovalFileValidating] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success');
  // Drawer states
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showEscortModal, setShowEscortModal] = useState(false);
  const [showMapDrawer, setShowMapDrawer] = useState(false);
  const [mapKey, setMapKey] = useState(0);

  const [selectedVehicles, setSelectedVehicles] = useState<VehicleFirst[]>([]);
  const [selectedDrivers, setSelectedDrivers] = useState<any[]>([]);
  const [selectedEscorts, setSelectedEscorts] = useState<any[]>([]);
  const [selectedPoc, setSelectedPoc] = useState<any | null>(null);
  const [employeeModalMode, setEmployeeModalMode] = useState<
    'poc' | 'drivers' | 'escorts' | null
  >(null);

  const { data: Vehicles } = useGetVehiclesListQuery(user?.company?._id);
  const { data: Employees } = useGetActiveEmployeeListQuery(user?.company?._id);
  const [createOrder] = useCreateOrderMutation();
  const [updateOrder] = useUpdateOrderMutation();

  // Error state including origin
  const [errors, setErrors] = useState<{
    application?: string;
    shipmentType?: string;
    origin?: string;
    destination?: string;
    validFrom?: string;
    validTo?: string;
    shipmentDetails?: string;
    vehicles?: string;
    drivers?: string;
    escorts?: string;
    eventLetterFile?: string;
    eventLayoutFile?: string;
    siraApprovalFile?: string;
    trips?: string;
    [key: string]: string | undefined;
  }>({});

  // Helper to clear errors when fields are updated
  const clearError = (field: keyof typeof errors) => {
    setErrors((prev) => {
      if (prev[field]) {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      }
      return prev;
    });
  };

  // Utility to open the map drawer for selecting origin or destination
  const openMapDrawer = (type: 'origin' | 'destination') => {
    setLocationSelectionType(type);
    setMapKey((prevKey) => prevKey + 1);
    setShowMapDrawer(true);
  };

  // ----- Map/Autocomplete Logic -----
  const onLoadMap = (map: google.maps.Map) => {
    setMapInstance(map);
  };

  const onLoadAutocomplete = (auto: google.maps.places.Autocomplete) => {
    setAutocomplete(auto);
  };

  useEffect(() => {
    const tripCount = trips || 1; // fallback for null
    setShipments((prev) => {
      const next: ShipmentGroup[] = [];
      for (let i = 0; i < tripCount; i++) {
        next[i] = prev[i] || { shipmentDetails: [EMPTY_DETAIL] };
      }
      return next;
    });
  }, [trips]);

  const addDetailRow = useCallback((i: number) => {
    setShipments((prev) => {
      const next = [...prev];
      next[i] = {
        shipmentDetails: [...next[i].shipmentDetails, EMPTY_DETAIL],
      };
      return next;
    });
  }, []);

  const updateDetail = useCallback(
    (i: number, j: number, field: keyof ShipmentDetail, value: string) => {
      setShipments((prev) => {
        const next = [...prev];
        const details = [...next[i].shipmentDetails];
        details[j] = { ...details[j], [field]: value };
        next[i] = { shipmentDetails: details };
        return next;
      });
    },
    []
  );

  const removeDetailRow = useCallback((i: number, j: number) => {
    setShipments((prev) => {
      const next = [...prev];
      const details = next[i].shipmentDetails.filter((_, idx) => idx !== j);
      next[i] = { shipmentDetails: details.length ? details : [EMPTY_DETAIL] };
      return next;
    });
  }, []);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: string
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      switch (fileType) {
        case 'eventLetterFile':
          setEventLetterFile(file);
          break;
        case 'eventLayoutFile':
          setEventLayoutFile(file);
          break;
        case 'siraApprovalFile':
          setSiraApprovalFile(file);
          break;
        default:
          console.warn('Unknown file type:', fileType);
      }
    }
  };

  const onPlaceChanged = () => {
    // Called when user selects a place from the Autocomplete dropdown
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        // Use the formatted address or name if available; otherwise, fallback to lat lng
        const placeName =
          place.formatted_address ||
          place.name ||
          `${lat.toFixed(5)}, ${lng.toFixed(5)}`;


        if (locationSelectionType === 'origin') {
          setOriginLocation({ lat, lng });
          setSelectedOriginName(placeName);
          clearError('origin');
        } else {
          setDestination({ lat, lng });
          setSelectedPlaceName(placeName);
          clearError('destination');
        }
        if (place.geometry.viewport) {
          mapInstance?.fitBounds(place.geometry.viewport);
        } else {
          mapInstance?.setZoom(17);
          mapInstance?.panTo({ lat, lng });
        }
      }
    }
  };

  // Updated handleMapClick function to differentiate origin and destination
  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    if (locationSelectionType === 'origin') {
      setOriginLocation({ lat, lng });
      clearError('origin');
    } else {
      setDestination({ lat, lng });
      clearError('destination');
    }

    // Attempt reverse geocoding
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      let placeName = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      if (status === 'OK' && results && results[0]) {
        placeName = results[0].formatted_address;
      }
      
      if (locationSelectionType === 'origin') {
        setSelectedOriginName(placeName);
      } else {
        setSelectedPlaceName(placeName);
      }
    });
  };

  const isRowPopulated = (row: ShipmentDetail) =>
    Object.values(row).some((val) => val.trim() !== '');

  // ----- Checkboxes Logic -----
  const handleCheckboxChange = (
    value: string,
    setState: React.Dispatch<React.SetStateAction<string>>,
    errorField: keyof typeof errors
  ) => {
    setState((prev) => {
      const newValue = prev === value ? '' : value;
      if (newValue.trim()) {
        clearError(errorField);
      }
      return newValue;
    });
  };

  // ----- Vehicle Logic -----
  const toggleVehicleModal = () => setShowVehicleModal(!showVehicleModal);
  const handleVehicleCheckbox = (vehicle: VehicleFirst) => {
    setSelectedVehicles((prev) => {
      const alreadySelected = prev.some((v) => v._id === vehicle._id);
      const newArr = alreadySelected
        ? prev.filter((v) => v._id !== vehicle._id)
        : [...prev, vehicle];

      if (newArr.length > 0) {
        clearError('vehicles');
      }

      return newArr;
    });
  };

  const handleSelectAllVehicles = () => {
    if (
      selectedVehicles.length ===
      (Vehicles ? (Vehicles as VehicleFirst[]).length : 0)
    ) {
      setSelectedVehicles([]);
    } else {
      const all = Vehicles || [];
      setSelectedVehicles(all);
      if (all.length > 0) {
        clearError('vehicles');
      }
    }
  };

  const handleClearAllVehicles = () => {
    setSelectedVehicles([]);
  };

  const openEmployeeModal = (mode: 'poc' | 'drivers' | 'escorts') => {
    setEmployeeModalMode(mode);
    setShowEmployeeModal(true);
  };

  const closeEmployeeModal = () => {
    setEmployeeModalMode(null);
    setShowEmployeeModal(false);
  };

  // ----- Employees Logic -----
  const handleEmployeesCheckbox = (employee: any) => {
    if (!employeeModalMode) return;

    if (employeeModalMode === 'poc') {
      setSelectedPoc(employee);
      clearError('poc');
      return;
    }

    const isDriver = employeeModalMode === 'drivers';
    const selected = isDriver ? selectedDrivers : selectedEscorts;
    const setSelected = isDriver ? setSelectedDrivers : setSelectedEscorts;

    const alreadySelected = selected.some((e) => e._id === employee._id);
    const updated = alreadySelected
      ? selected.filter((e) => e._id !== employee._id)
      : [...selected, employee];

    if (updated.length > 0) {
      clearError(employeeModalMode);
    }

    setSelected(updated);
  };

  const handleSelectAllEmployees = () => {
    if (!employeeModalMode || employeeModalMode === 'poc') return;

    const isDriver = employeeModalMode === 'drivers';
    const selected = isDriver ? selectedDrivers : selectedEscorts;
    const setSelected = isDriver ? setSelectedDrivers : setSelectedEscorts;

    const all = Employees?.response || [];
    const isAllSelected = selected.length === all.length;

    const newList = isAllSelected ? [] : all;
    if (newList.length > 0) {
      clearError(employeeModalMode);
    }

    setSelected(newList);
  };

  // ----- Chips Removal Logic -----
  const handleRemoveVehicleChip = (vehicleId: string) => {
    setSelectedVehicles((prev) => {
      const newArr = prev.filter((v) => v._id !== vehicleId);
      if (newArr.length > 0) {
        clearError('vehicles');
      }
      return newArr;
    });
  };
  const handleRemoveDriverChip = (employeeId: string) => {
    setSelectedDrivers((prev) => {
      const updated = prev.filter((e) => e._id !== employeeId);
      if (updated.length > 0) clearError('drivers');
      return updated;
    });
  };

  const handleRemoveEscortChip = (employeeId: string) => {
    setSelectedEscorts((prev) => {
      const updated = prev.filter((e) => e._id !== employeeId);
      if (updated.length > 0) clearError('escorts');
      return updated;
    });
  };

  // ----- Map Drawer -----
  const toggleMapDrawer = () => setShowMapDrawer(!showMapDrawer);

  const validateForm = () => {
    const newErrors: {
      application?: string;
      shipmentType?: string;
      origin?: string;
      destination?: string;
      validFrom?: string;
      validTo?: string;
      shipmentDetails?: string;
      vehicles?: string;
      drivers?: string;
      escorts?: string;
      eventLetterFile?: string;
      eventLayoutFile?: string;
      siraApprovalFile?: string;
      trips?: string;
    } = {};

    if (!selectedApplications.trim()) {
      newErrors.application = t('create_order.validation.application');
    }

    if (!selectedShipmentTypes.trim()) {
      newErrors.shipmentType = t('create_order.validation.shipment_type');
    }

    if (!originLocation) {
      newErrors.origin = t('create_order.validation.origin');
    }

    if (!destination) {
      newErrors.destination = t('create_order.validation.destination');
    }

    if (!validFrom) {
      newErrors.validFrom = t('create_order.validation.valid_from');
    }

    if (!validTo) {
      newErrors.validTo = t('create_order.validation.valid_to');
    }

    if (selectedVehicles.length === 0) {
      newErrors.vehicles = t('create_order.validation.vehicles');
    }

    if (selectedDrivers.length === 0) {
      newErrors.drivers = t('create_order.validation.drivers');
    }

    if (selectedEscorts.length === 0) {
      newErrors.escorts = t('create_order.validation.escorts');
    }

    if (!eventLetterFile) {
      newErrors.eventLetterFile = t(
        'create_order.validation.event_letter_file'
      );
    }

    if (!eventLayoutFile) {
      newErrors.eventLayoutFile = t(
        'create_order.validation.event_layout_file'
      );
    }

    // Optional or required depending on logic?
    // Let's make it optional unless instructed otherwise, but eventLayoutFile is required.
    // Wait, the user didn't specify. I will not add it to required fields unless requested, or maybe I should?
    // Let's add it. No wait, I shouldn't make it required if not specified.
    // Wait, let's keep it optional for now, or match eventLayoutFile. Let's make it optional.


    if (!trips || trips < 1) {
      newErrors.trips = 'Trips must be at least 1';
    }

    // Ensure EVERY shipment row is completely filled
    const invalidShipment = shipments.some(
      (group) =>
        group.shipmentDetails.length === 0 ||
        group.shipmentDetails.some(
          (detail) =>
            !detail.description ||
            !detail.hd ||
            !detail.unSNo ||
            !detail.boxesQty ||
            !detail.maxSize ||
            !detail.netWt ||
            !detail.grossWt ||
            !detail.maxHt
        )
    );
    if (invalidShipment) {
      newErrors.shipmentDetails = t('create_order.validation.shipment') || 'Please fill out all shipment details';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      isEventLetterFileValidating ||
      isEventLayoutFileValidating ||
      isSiraApprovalFileValidating
    ) {
      addToast({
        title: 'Please wait',
        description: 'Still validating uploaded file(s). Please try again in a moment.',
        color: 'warning',
        severity: 'warning',
        variant: 'bordered',
        radius: 'lg',
        timeout: 3000,
        shouldShowTimeoutProgress: true,
      });
      return;
    }

    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    // Construct the payload using the selected origin
    const origin = {
      type: 'Point',
      coordinates: [originLocation!.lng, originLocation!.lat],
    };

    const dest = {
      type: 'Point',
      coordinates: [destination!.lng, destination!.lat],
    };

    // Convert DateValue to timestamp in milliseconds
    const validFromTimestamp = validFrom?.toDate().getTime().toString();
    const validToTimestamp = validTo?.toDate().getTime().toString();
    const eventDateToTimestamp = eventDate?.toDate().getTime().toString();

    const possibleVehicles = selectedVehicles.map((v) => v._id);

    const payload = {
      origin: origin,
      destination: dest,
      validFrom: validFromTimestamp,
      validTo: validToTimestamp,
      eventDate: eventDateToTimestamp,
      possibleVehicles: possibleVehicles,
      possibleDrivers: selectedDrivers.map((d) => d._id),
      possibleEscorts: selectedEscorts.map((e) => e._id),
      poc: selectedPoc?._id || '',
      shipments: shipments.map((grp) => ({
        shipmentDetails: grp.shipmentDetails.map((sd) => ({
          description: sd.description,
          hd: parseFloat(sd.hd) || 0,
          unSlNo: sd.unSNo,
          boxesQty: parseFloat(sd.boxesQty) || 0,
          netWeight: parseFloat(sd.netWt) || 0,
          grossWeight: parseFloat(sd.grossWt) || 0,
          maxSize: parseFloat(sd.maxSize) || 0,
          maxHt: parseFloat(sd.maxHt) || 0,
        })),
      })),
      application: selectedApplications,
      orderType: selectedShipmentTypes,
      trips,
      status: 'draft',
      address: {
        origin: selectedOriginName,
        destination: selectedPlaceName,
      },
    };


    try {
      const response = await createOrder(payload).unwrap();
      if (eventLetterFile || eventLayoutFile || siraApprovalFile) {
        try {
          const orderId = response._id || response.data?._id || response.id;
          await updateOrder({
            id: orderId,
            eventLetterFile: eventLetterFile || undefined,
            eventLayoutFile: eventLayoutFile || undefined,
            siraApprovalFile: siraApprovalFile || undefined,
          }).unwrap();
        } catch (fileError) {
          console.error('Error updating files:', fileError);
          addToast({
            title: 'Warning',
            description:
              'Order was created, but attaching the uploaded file(s) failed. Please open the order and re-upload them.',
            color: 'danger',
            severity: 'danger',
            variant: 'bordered',
            radius: 'lg',
            timeout: 5000,
            shouldShowTimeoutProgress: true,
          });
        }
      }

      addToast({
        title: 'Success!',
        description: 'Order created successfully!',
        color: 'success',
        severity: 'success',
        variant: 'bordered',
        radius: 'lg',
        timeout: 3000,
        shouldShowTimeoutProgress: true,
      });
      if (onOrderCreated) {
        onOrderCreated();
      }
      if (onClose) {
        onClose();
      }
    } catch (error: any) {
      console.error('Error creating order:', error);
      const errorMsg = error?.data?.message;
      addToast({
        title: 'Error',
        description:
          typeof errorMsg === 'string' && errorMsg
            ? errorMsg
            : 'Failed to create order. Please try again.',
        color: 'danger',
        severity: 'danger',
        variant: 'bordered',
        radius: 'lg',
        timeout: 5000,
        shouldShowTimeoutProgress: true,
      });
    }
  };

  // ----- Options -----
  const applicationOptions = [
    { value: 'reshipment', label: `${t('create_order.reshipment')}` },
    { value: 'export', label: `${t('create_order.export')}` },
    { value: 'transshipment', label: `${t('create_order.transshipment')}` },
    { value: 'transit', label: `${t('create_order.transit')}` },
    { value: 'movement', label: `${t('create_order.movement')}` },
    { value: 'import', label: `${t('create_order.import')}` },
    { value: 'clearance', label: `${t('create_order.release_clearance')}` },
  ];

  const shipmentTypeOptions = [
    {
      value: 'military equipment',
      label: `${t('create_order.military_equipment')}`,
    },
    { value: 'other DGS', label: `${t('create_order.other_dgs')}` },
    { value: 'chemicals', label: `${t('create_order.chemicals')}` },
    { value: 'imco class 1', label: `${t('create_order.imco_class_1')}` },
  ];

  return (
    <div className="ps-4 pt-2 pb-6">
      <form
        className="w-full flex flex-col gap-8"
        onSubmit={onSubmit}
        aria-label="Create Order Form"
      >
        {/* Shipment Type Checkboxes */}
        <div className="flex flex-col gap-8 items-start">
          <p className={formLabelClass}>{t('create_order.shipment_type')}</p>
          <div className="flex flex-row flex-wrap lg:flex-nowrap space-x-4">
            {shipmentTypeOptions.map((option) => (
              <Checkbox
                color="primary"
                key={option.value}
                value={option.value}
                isSelected={selectedShipmentTypes === option.value}
                onChange={() =>
                  handleCheckboxChange(
                    option.value,
                    setSelectedShipmentTypes,
                    'shipmentType'
                  )
                }
                classNames={{
                  label: 'text-[var(--text-main)] whitespace-nowrap mr-4',
                }}
              >
                {option.label}
              </Checkbox>
            ))}
          </div>
          {errors.shipmentType && (
            <span className="text-[var(--color-error)] text-sm">{errors.shipmentType}</span>
          )}
        </div>

        <Divider className={formDividerClass} />

        <div className="flex flex-col gap-2">
          <label className={formLabelClass}>
            {t('create_order.point_of_contact')}
          </label>
          <Button
            onPress={() => openEmployeeModal('poc')}
            className={secondaryActionButtonClass}
          >
            {t('create_order.select_poc')}
          </Button>
          {/* Display selected POC */}
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedPoc && (
              <Chip
                variant="bordered"
                key={selectedPoc._id}
                className="text-[var(--text-main)] py-4"
                onClose={() => setSelectedPoc(null)}
                color="primary"
              >
                {`${selectedPoc.firstName} ${selectedPoc.lastName}`}
              </Chip>
            )}
          </div>
          {errors.drivers && (
            <span className="text-[var(--color-error)] text-sm">{errors.drivers}</span>
          )}
        </div>

        <Divider className={formDividerClass} />

        {/* Origin & Destination Row */}
        <div className="flex flex-col md:flex-row gap-8 lg:gap-20 w-full">
          {/* Origin */}
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <label className={formLabelClass}>{t('orders_page.origin')}</label>
            <div className="flex items-center gap-4">
              {selectedOriginName && (
                <p className="text-[var(--text-main)] truncate flex-1" title={selectedOriginName}>
                  {selectedOriginName}
                </p>
              )}
              <Button
                type="button"
                className={`${secondaryActionButtonClass} shrink-0`}
                onPress={() => openMapDrawer('origin')}
                startContent={
                  selectedOriginName && (
                    <img
                      src="/assets/icons/pen.svg"
                      alt="edit"
                      className="mr-1"
                    />
                  )
                }
              >
                {selectedOriginName
                  ? `${t('order_details.change')}`
                  : `${t('create_order.select_origin')}`}
              </Button>
            </div>
            {errors.origin && (
              <span className="text-[var(--color-error)] text-sm">{errors.origin}</span>
            )}
          </div>

          {/* Destination */}
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <label className={formLabelClass}>
              {t('orders_page.destination')}
            </label>
            <div className="flex items-center gap-4">
              {selectedPlaceName && (
                <p className="text-[var(--text-main)] truncate flex-1" title={selectedPlaceName}>
                  {selectedPlaceName}
                </p>
              )}
              <Button
                type="button"
                className={`${secondaryActionButtonClass} shrink-0`}
                onPress={() => openMapDrawer('destination')}
                startContent={
                  selectedPlaceName && (
                    <img
                      src="/assets/icons/pen.svg"
                      alt="edit"
                      className="mr-1"
                    />
                  )
                }
              >
                {selectedPlaceName
                  ? `${t('order_details.change')}`
                  : `${t('create_order.select_destination')}`}
              </Button>
            </div>
            {errors.destination && (
              <span className="text-[var(--color-error)] text-sm">{errors.destination}</span>
            )}
          </div>
        </div>

        <Divider className={formDividerClass} />

        <div className="flex flex-col gap-2 max-w-xs">
          <label htmlFor="trips" className={formLabelClass}>
            Trips
          </label>
          <Input
            id="trips"
            type="number"
            placeholder="Add trips"
            min={1}
            max={10}
            value={trips === null ? '' : trips}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                setTrips(null);
                return;
              }

              const num = Number(val);
              if (!isNaN(num)) {
                setTrips(num);
              }
            }}
            onBlur={(e) => {
              const num = Number(e.target.value);
              if (isNaN(num) || num < 1) {
                setTrips(1);
              } else if (num > 10) {
                setTrips(10);
              } else {
                setTrips(num);
              }
            }}
            inputClassName="border border-[var(--border-light)] shadow-sm text-[var(--text-main)] placeholder:text-[var(--text-muted)] max-w-24"
            aria-label="trips"
          />
          {errors.trips && (
            <span className="text-[var(--color-error)] text-sm">{errors.trips}</span>
          )}
        </div>

        <Divider className={formDividerClass} />

        {/* Shipment Details Section */}
        {shipments.map((grp, i) => (
          <div key={i} className="mb-8">
            <h3 className="mb-3 text-base font-semibold text-[var(--text-main)]">
              Shipment {i + 1}
            </h3>
            <div
              dir="ltr"
              className="overflow-x-auto overflow-y-hidden rounded-xl border border-[var(--border-light)] bg-[var(--bg-surface)] shadow-sm"
            >
              <table className="min-w-[1080px] w-full table-fixed border-separate border-spacing-0 text-sm">
                <colgroup>
                  <col className="w-[110px]" />
                  <col className="w-[220px]" />
                  <col className="w-[100px]" />
                  <col className="w-[120px]" />
                  <col className="w-[110px]" />
                  <col className="w-[110px]" />
                  <col className="w-[110px]" />
                  <col className="w-[110px]" />
                  <col className="w-[110px]" />
                  <col className="w-[72px]" />
                </colgroup>
                <thead className="bg-[var(--bg-surface-2)]">
                  <tr>
                    <th className="rounded-tl-xl border-b border-[var(--border-light)] px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                      Item No
                    </th>
                    <th className="border-b border-[var(--border-light)] px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                      Description
                    </th>
                    <th className="border-b border-[var(--border-light)] px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                      H.D.
                    </th>
                    <th className="border-b border-[var(--border-light)] px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                      UN S/No
                    </th>
                    <th className="border-b border-[var(--border-light)] px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                      Boxes Qty
                    </th>
                    <th className="border-b border-[var(--border-light)] px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                      Max Size
                    </th>
                    <th className="border-b border-[var(--border-light)] px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                      Net Wt.
                    </th>
                    <th className="border-b border-[var(--border-light)] px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                      Gross Wt.
                    </th>
                    <th className="border-b border-[var(--border-light)] px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                      Max Ht (m)
                    </th>
                    <th className="rounded-tr-xl border-b border-[var(--border-light)] px-3 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {grp.shipmentDetails.map((row, j) => (
                    <tr key={j} className="border-b border-[var(--border-light)] last:border-b-0">
                      <td className="px-4 py-4 align-top">
                        <Input
                          type="text"
                          value={(j + 1).toString()}
                          disabled
                          onChange={(e) =>
                            updateDetail(i, j, 'itemNo', e.target.value)
                          }
                          inputClassName={`${shipmentNumberInputClass} w-full min-w-0`}
                          className="mb-0"
                          aria-label="itemNo"
                        />
                      </td>
                      <td className="px-4 py-4 align-top">
                        <Input
                          type="text"
                          value={row.description}
                          onChange={(e) => {
                            updateDetail(i, j, 'description', e.target.value);
                            if (errors.shipmentDetails) setErrors((prev) => ({ ...prev, shipmentDetails: undefined }));
                          }}
                          inputClassName={`${shipmentInputClass} w-full min-w-0`}
                          className="mb-0"
                          error={errors.shipmentDetails && !row.description ? (true as any) : undefined}
                          errorText=""
                          aria-label="description"
                        />
                      </td>
                      <td className="px-4 py-4 align-top">
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={row.hd}
                          onChange={(e) => {
                            updateDetail(i, j, 'hd', e.target.value);
                            if (errors.shipmentDetails) setErrors((prev) => ({ ...prev, shipmentDetails: undefined }));
                          }}
                          inputClassName={`${shipmentInputClass} w-full min-w-0`}
                          className="mb-0"
                          error={errors.shipmentDetails && !row.hd ? (true as any) : undefined}
                          errorText=""
                          aria-label="hd"
                        />
                      </td>
                      <td className="px-4 py-4 align-top">
                        <Input
                          type="text"
                          value={row.unSNo}
                          onChange={(e) => {
                            updateDetail(i, j, 'unSNo', e.target.value);
                            if (errors.shipmentDetails) setErrors((prev) => ({ ...prev, shipmentDetails: undefined }));
                          }}
                          inputClassName={`${shipmentInputClass} w-full min-w-0`}
                          className="mb-0"
                          error={errors.shipmentDetails && !row.unSNo ? (true as any) : undefined}
                          errorText=""
                          aria-label="unSNo"
                        />
                      </td>
                      <td className="px-4 py-4 align-top">
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={row.boxesQty}
                          onChange={(e) => {
                            updateDetail(i, j, 'boxesQty', e.target.value);
                            if (errors.shipmentDetails) setErrors((prev) => ({ ...prev, shipmentDetails: undefined }));
                          }}
                          inputClassName={`${shipmentInputClass} w-full min-w-0`}
                          className="mb-0"
                          error={errors.shipmentDetails && !row.boxesQty ? (true as any) : undefined}
                          errorText=""
                          aria-label="boxesQty"
                        />
                      </td>
                      <td className="px-4 py-4 align-top">
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={row.maxSize || ''}
                          onChange={(e) => {
                            updateDetail(i, j, 'maxSize', e.target.value);
                            if (errors.shipmentDetails) setErrors((prev) => ({ ...prev, shipmentDetails: undefined }));
                          }}
                          inputClassName={`${shipmentInputClass} w-full min-w-0`}
                          className="mb-0"
                          error={errors.shipmentDetails && !row.maxSize ? (true as any) : undefined}
                          errorText=""
                          aria-label="maxSize"
                        />
                      </td>
                      <td className="px-4 py-4 align-top">
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={row.netWt}
                          onChange={(e) => {
                            updateDetail(i, j, 'netWt', e.target.value);
                            if (errors.shipmentDetails) setErrors((prev) => ({ ...prev, shipmentDetails: undefined }));
                          }}
                          inputClassName={`${shipmentInputClass} w-full min-w-0`}
                          className="mb-0"
                          error={errors.shipmentDetails && !row.netWt ? (true as any) : undefined}
                          errorText=""
                          aria-label="netWt"
                        />
                      </td>
                      <td className="px-4 py-4 align-top">
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={row.grossWt}
                          onChange={(e) => {
                            updateDetail(i, j, 'grossWt', e.target.value);
                            if (errors.shipmentDetails) setErrors((prev) => ({ ...prev, shipmentDetails: undefined }));
                          }}
                          inputClassName={`${shipmentInputClass} w-full min-w-0`}
                          className="mb-0"
                          error={errors.shipmentDetails && !row.grossWt ? (true as any) : undefined}
                          errorText=""
                          aria-label="grossWt"
                        />
                      </td>
                      <td className="px-4 py-4 align-top">
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={row.maxHt || ''}
                          onChange={(e) => {
                            updateDetail(i, j, 'maxHt', e.target.value);
                            if (errors.shipmentDetails) setErrors((prev) => ({ ...prev, shipmentDetails: undefined }));
                          }}
                          inputClassName={`${shipmentInputClass} w-full min-w-0`}
                          className="mb-0"
                          error={errors.shipmentDetails && !row.maxHt ? (true as any) : undefined}
                          errorText=""
                          aria-label="maxHt"
                        />
                      </td>
                      <td className="px-3 py-4 align-top">
                        <div className="flex h-12 items-center justify-center">
                          <Button
                            variant="flat"
                            type="button"
                            size="sm"
                            onPress={() => removeDetailRow(i, j)}
                            className="border border-[var(--border-light)] bg-[var(--bg-surface-2)] text-[var(--text-muted)] hover:bg-[var(--color-error)]/10 hover:text-[var(--color-error)]"
                            isIconOnly
                          >
                            <FaXmark />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end mt-2">
              <Button
                type="button"
                onPress={() => addDetailRow(i)}
                className={secondaryActionButtonClass}
                startContent={<FaPlus />}
              >
                {t('create_order.add_more')}
              </Button>
            </div>
            {i === shipments.length - 1 && errors.shipmentDetails && (
              <span className="text-[var(--color-error)] text-sm mt-2 block">
                {errors.shipmentDetails}
              </span>
            )}
          </div>
        ))}

        <Divider className={formDividerClass} />

        {/* Valid Date */}
        <div className="flex flex-col gap-4 max-w-xl">
          <p className={formLabelClass}>
            {t('orders_page.valid_from') || 'Valid From'}
          </p>
          <div className="flex flex-row max-w-sm flex-wrap lg:flex-nowrap space-x-4">
            <DatePicker
              variant="flat"
              size="md"
              isRequired
              hideTimeZone
              minValue={today(getLocalTimeZone())}
              showMonthAndYearPickers
              defaultValue={validFrom}
              onChange={(val) => {
                setValidFrom(val);
                if (val) {
                  clearError('validFrom');
                }
              }}
              classNames={stylesDateInput}
            />
          </div>
          {errors.validFrom && (
            <span className="text-[var(--color-error)] text-sm">{errors.validFrom}</span>
          )}
        </div>

        <div className="flex flex-col gap-4 max-w-xl">
          <p className={formLabelClass}>
            {t('orders_page.valid_to') || 'Valid To'}
          </p>
          <div className="flex flex-row max-w-sm flex-wrap lg:flex-nowrap space-x-4">
            <DatePicker
              variant="flat"
              size="md"
              isRequired
              hideTimeZone
              minValue={validFrom || today(getLocalTimeZone())}
              showMonthAndYearPickers
              defaultValue={validTo}
              onChange={(val) => {
                setValidTo(val);
                if (val) {
                  clearError('validTo');
                }
              }}
              classNames={stylesDateInput}
            />
          </div>
          {errors.validTo && (
            <span className="text-[var(--color-error)] text-sm">{errors.validTo}</span>
          )}
        </div>

        <Divider className={formDividerClass} />

        <div className="flex flex-col gap-4 max-w-xl">
          <p className={formLabelClass}>{t('orders_page.event_date')}</p>
          <div className="flex flex-row max-w-sm flex-wrap lg:flex-nowrap space-x-4">
            <DatePicker
              variant="flat"
              size="md"
              isRequired
              hideTimeZone
              minValue={validFrom ?? today(getLocalTimeZone())}
              maxValue={validTo ?? undefined}
              showMonthAndYearPickers
              defaultValue={eventDate}
              onChange={(val) => {
                setEventDate(val);
                clearError('eventDate');
              }}
              classNames={stylesDateInput}
              isDisabled={!validFrom || !validTo}
            />
          </div>
          {errors.validTo && (
            <span className="text-[var(--color-error)] text-sm">{errors.validTo}</span>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <label className={formLabelClass}>
            {t('create_order.event_letter_file')}
          </label>
          <FileUpload
            id="eventLetterFile"
            name="eventLetterFile"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFileChange(e, 'eventLetterFile')
            }
            onValidatingChange={setIsEventLetterFileValidating}
            inputClassName={fileUploadInputClass}
            pickerClassName="max-w-80"
            placeholder={t('create_order.upload_event_letter')}
            allowedFileTypes={['application/pdf']}
            maxFileSize={1024 * 1024}
          />
          {errors.eventLetterFile && (
            <span className="text-[var(--color-error)] text-sm">
              {errors.eventLetterFile}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <label className={formLabelClass}>
            {t('create_order.event_layout_file')}
          </label>
          <FileUpload
            id="eventLayoutFile"
            name="eventLayoutFile"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFileChange(e, 'eventLayoutFile')
            }
            onValidatingChange={setIsEventLayoutFileValidating}
            inputClassName={fileUploadInputClass}
            placeholder={t('create_order.upload_event_layout')}
            pickerClassName="max-w-80"
            allowedFileTypes={['application/pdf']}
            maxFileSize={1024 * 1024}
          />
          {errors.eventLayoutFile && (
            <span className="text-[var(--color-error)] text-sm">
              {errors.eventLayoutFile}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <label className={formLabelClass}>
            {t('create_order.sira_approval_file') || "Sira Approval File"}
          </label>
          <FileUpload
            id="siraApprovalFile"
            name="siraApprovalFile"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFileChange(e, 'siraApprovalFile')
            }
            onValidatingChange={setIsSiraApprovalFileValidating}
            inputClassName={fileUploadInputClass}
            placeholder={t('create_order.upload_sira_approval') || "Upload Sira Approval File"}
            pickerClassName="max-w-80"
            allowedFileTypes={['application/pdf']}
            maxFileSize={1024 * 1024}
          />
          {errors.siraApprovalFile && (
            <span className="text-[var(--color-error)] text-sm">
              {errors.siraApprovalFile}
            </span>
          )}
        </div>

        {/* Vehicle & Driver & Escort Selection */}
        <div className="flex flex-col gap-8 max-w-lg">
          {/* Vehicle Section */}
          <div className="flex flex-col gap-2">
            <label className={formLabelClass}>
              {t('create_order.vehicles_details')}
            </label>
            <div className="flex items-center gap-2">
              <Button
                onPress={toggleVehicleModal}
                className={secondaryActionButtonClass}
              >
                {t('create_order.select_vehicles')}
              </Button>
              {selectedVehicles.length > 0 && (
                <Button
                  type="button"
                  variant="light"
                  onPress={handleClearAllVehicles}
                  className="text-[var(--color-error)] hover:bg-[var(--color-error)]/10"
                >
                  {t('button.clear_all')}
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedVehicles.map((vehicle) => (
                <Chip
                  variant="bordered"
                  key={vehicle._id}
                  className="text-[var(--text-main)] py-4"
                  onClose={() => handleRemoveVehicleChip(vehicle._id)}
                  color="primary"
                >
                  {vehicle.plateNumber}
                </Chip>
              ))}
            </div>
            {errors.vehicles && (
              <span className="text-[var(--color-error)] text-sm">{errors.vehicles}</span>
            )}
          </div>

          {/* Driver Section */}
          <div className="flex flex-col gap-2">
            <label className={formLabelClass}>
              {t('create_order.drivers_details')}
            </label>
            <Button
              onPress={() => openEmployeeModal('drivers')}
              className={secondaryActionButtonClass}
            >
              {t('create_order.select_drivers')}
            </Button>
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedDrivers.map((employee) => (
                <Chip
                  variant="bordered"
                  key={employee._id}
                  className="text-[var(--text-main)] py-4"
                  onClose={() => handleRemoveDriverChip(employee._id)}
                  color="primary"
                >
                  {`${employee.firstName} ${employee.lastName}`}
                </Chip>
              ))}
            </div>
            {errors.drivers && (
              <span className="text-[var(--color-error)] text-sm">{errors.drivers}</span>
            )}
          </div>

          {/* Escort Section */}
          <div className="flex flex-col gap-2">
            <label className={formLabelClass}>
              {t('escort.escort_details')}
            </label>
            <Button
              onPress={() => openEmployeeModal('escorts')}
              className={secondaryActionButtonClass}
            >
              {t('create_order.select_escorts')}
            </Button>
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedEscorts.map((employee) => (
                <Chip
                  variant="bordered"
                  key={employee._id}
                  className="text-[var(--text-main)] py-4"
                  onClose={() => handleRemoveEscortChip(employee._id)}
                  color="primary"
                >
                  {`${employee.firstName} ${employee.lastName}`}
                </Chip>
              ))}
            </div>
            {errors.escorts && (
              <span className="text-[var(--color-error)] text-sm">{errors.escorts}</span>
            )}
          </div>
        </div>

        <Divider className={formDividerClass} />

        <div className="flex flex-col gap-2 mt-10">
          <Checkbox
            isRequired
            color="primary"
            classNames={{
              label: 'text-[var(--text-main)] text-sm leading-relaxed',
            }}
          >
            نحن / <span className="font-bold">*</span> أنا أعلن أن المعلومات
            الواردة أعلاه صحيحة وسوف أقوم بإبلاغ سيرا على الفور ما إذا كان قد تم
            التغيير في أي من المعلومات المذكورة أعلاه.
            <br />
            <span className="block mt-2">
              We/I<span className="font-bold">*</span> declare that the
              information given above is true to the best of our/my
              <span className="font-bold">*</span> knowledge and undertake to
              inform SIRA immediately if any of the above information changes.
            </span>
          </Checkbox>
        </div>

        <div className="flex items-end justify-end mt-10">
          <Button
            type="submit"
            className="rounded-lg shadow-md font-semibold text-medium transition-colors"
            style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-invert)' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
          >
            {t('button.submit')}
          </Button>
        </div>
      </form>

      {/* Vehicle Modal */}
      <Drawer
        isOpen={showVehicleModal}
        onClose={toggleVehicleModal}
        placement={isRtl ? 'left' : 'right'}
        backdrop="opaque"
        className="bg-[var(--bg-surface)] shadow-xl border-s border-[var(--border-light)]"
        size="sm"
        classNames={{
          closeButton:
            'bg-[var(--bg-overlay)] m-2 hover:bg-[var(--bg-surface-2)] text-[var(--text-main)] p-2',
          header: 'px-6',
        }}
      >
        <DrawerContent>
          <>
            <DrawerHeader className="text-[var(--text-main)] text-xl font-bold">
              {t('create_order.select_vehicles')}
            </DrawerHeader>
            <Divider className={formDividerClass} />
            <DrawerBody className="py-6">
              {Vehicles && Vehicles.length > 0 && (
                <Checkbox
                  isSelected={
                    selectedVehicles.length ===
                    (Vehicles ? (Vehicles as VehicleFirst[]).length : 0)
                  }
                  onChange={handleSelectAllVehicles}
                  color="primary"
                  classNames={{
                    label: 'text-[var(--text-main)]',
                  }}
                >
                  {t('create_order.select_all')}
                </Checkbox>
              )}

              <div className="flex flex-col gap-2 mt-2">
                {Vehicles && Vehicles.length > 0 ? (
                  Vehicles?.map((vehicle: VehicleFirst) => {
                    const isSelected = selectedVehicles.some(
                      (v) => v._id === vehicle._id
                    );
                    return (
                      <div
                        key={vehicle._id}
                        className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                          isSelected
                            ? 'bg-[var(--color-primary-light)] border-[var(--color-primary)]'
                            : 'bg-[var(--bg-surface-2)] border-[var(--border-light)] hover:bg-[var(--bg-overlay)]'
                        }`}
                      >
                        <Checkbox
                          key={vehicle._id}
                          color="primary"
                          isSelected={isSelected}
                          onChange={() => handleVehicleCheckbox(vehicle)}
                          classNames={{
                            label: 'text-[var(--text-main)] p-1',
                          }}
                        >
                          {vehicle.plateNumber}
                        </Checkbox>
                      </div>
                    );
                  })
                ) : (
                  <div className="px-6 text-[var(--text-main)]">
                    <div className="flex flex-col items-center py-8 pb-4 mt-48">
                      <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center">
                        No vehicles found.
                        <p className="text-[var(--text-main)]/70 text-sm mt-4">
                          Please add vehicles to display them here.
                        </p>
                      </h3>
                      <div className="w-[100px] mt-2 mb-4 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                      <Button
                        onPress={() => navigate('/vehicles', { state: { openAdd: true } })}
                        className="rounded-lg shadow-md font-semibold text-medium transition-colors"
                        style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-invert)' }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
                      >
                        {t('vehicle.add_vehicle') || 'Add Vehicle'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </DrawerBody>
            <DrawerFooter className="m-auto">
              <Button
                onPress={toggleVehicleModal}
                className="rounded-lg shadow-md font-semibold text-medium mb-2 transition-colors"
                style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-invert)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
                startContent={
                  <img
                    src="/assets/icons/orders/jobDetails/tick_circle.svg"
                    className="w-5"
                  />
                }
              >
                {t('button.done')}
              </Button>
            </DrawerFooter>
          </>
        </DrawerContent>
      </Drawer>

      {/* Employee Modal */}
      <Drawer
        isOpen={showEmployeeModal}
        onClose={closeEmployeeModal}
        placement={isRtl ? 'left' : 'right'}
        backdrop="opaque"
        className="bg-[var(--bg-surface)] shadow-xl border-s border-[var(--border-light)]"
        size="sm"
        classNames={{
          closeButton:
            'bg-[var(--bg-overlay)] m-1 hover:bg-[var(--bg-surface-2)] text-[var(--text-main)] p-2',
        }}
      >
        <DrawerContent>
          <>
            <DrawerHeader className="text-[var(--text-main)] text-xl font-bold">
              {employeeModalMode === 'poc'
                ? t('create_order.select_poc')
                : employeeModalMode === 'drivers'
                ? t('create_order.select_drivers')
                : t('create_order.select_escorts')}
            </DrawerHeader>

            <DrawerBody className="py-6">
              {/* Select All checkbox only for drivers/escorts */}
              {Employees?.response?.length > 0 &&
                employeeModalMode !== 'poc' && (
                  <Checkbox
                    isSelected={
                      (employeeModalMode === 'drivers'
                        ? selectedDrivers.length
                        : selectedEscorts.length) === Employees.response.length
                    }
                    onChange={() => {
                      if (employeeModalMode === 'drivers') {
                        setSelectedDrivers(
                          selectedDrivers.length === Employees.response.length
                            ? []
                            : Employees.response
                        );
                      } else if (employeeModalMode === 'escorts') {
                        setSelectedEscorts(
                          selectedEscorts.length === Employees.response.length
                            ? []
                            : Employees.response
                        );
                      }
                    }}
                    color="primary"
                    classNames={{
                      label: 'text-[var(--text-main)]',
                    }}
                  >
                    {t('create_order.select_all')}
                  </Checkbox>
                )}

              <div className="flex flex-col gap-2 mt-2">
                {Employees?.response?.length > 0 ? (
                  Employees.response.map((employee: Employee) => {
                    const isSelected =
                      employeeModalMode === 'poc'
                        ? selectedPoc?._id === employee._id
                        : employeeModalMode === 'drivers'
                        ? selectedDrivers.some((d) => d._id === employee._id)
                        : selectedEscorts.some((e) => e._id === employee._id);

                    return (
                      <div
                        key={employee._id}
                        className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                          isSelected
                            ? 'bg-[var(--color-primary-light)] border-[var(--color-primary)]'
                            : 'bg-[var(--bg-surface-2)] border-[var(--border-light)] hover:bg-[var(--bg-overlay)]'
                        }`}
                      >
                        <Checkbox
                          color="primary"
                          isSelected={isSelected}
                          onChange={() => handleEmployeesCheckbox(employee)}
                          classNames={{
                            label: 'text-[var(--text-main)] p-1',
                          }}
                        >
                          {`${employee.firstName} ${employee.lastName}`}
                        </Checkbox>
                      </div>
                    );
                  })
                ) : (
                  <div className="px-6 text-[var(--text-main)]">
                    <div className="flex flex-col items-center py-8 pb-4 mt-48">
                      <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center">
                        No employees found.
                        <p className="text-[var(--text-main)]/70 text-sm mt-4">
                          Please add employees to display them here.
                        </p>
                      </h3>
                      <div className="w-[100px] mt-2 mb-4 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                      <Button
                        onPress={() => navigate('/employees', { state: { openAdd: true } })}
                        className="rounded-lg shadow-md font-semibold text-medium transition-colors"
                        style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-invert)' }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
                      >
                        {t('employee.add_employee') || 'Add Employee'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </DrawerBody>

            <DrawerFooter className="m-auto">
              <Button
                onPress={closeEmployeeModal}
                className="rounded-lg shadow-md font-semibold text-medium mb-2 transition-colors"
                style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-invert)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
                startContent={
                  <img
                    src="/assets/icons/orders/jobDetails/tick_circle.svg"
                    className="w-5"
                  />
                }
              >
                {t('button.done')}
              </Button>
            </DrawerFooter>
          </>
        </DrawerContent>
      </Drawer>

      {/* Map Drawer */}
      <Drawer
        isOpen={showMapDrawer}
        onClose={toggleMapDrawer}
        placement={isRtl ? 'left' : 'right'}
        backdrop="opaque"
        isDismissable={false}
        className="bg-[var(--bg-surface)] shadow-xl border-s border-[var(--border-light)]"
        classNames={{
          closeButton:
            'bg-[var(--bg-overlay)] m-1 hover:bg-[var(--bg-surface-2)] text-[var(--text-main)] p-2',
        }}
        size="lg"
      >
        <DrawerContent>
          <>
            <DrawerHeader className="text-[var(--text-main)] text-xl font-bold">
              {locationSelectionType === 'origin'
                ? t('create_order.select_origin')
                : t('create_order.select_destination')}
            </DrawerHeader>
            <DrawerBody className="py-6">
              <div className="mb-4">
                {isLoaded ? (
                  <Autocomplete
                    onLoad={onLoadAutocomplete}
                    onPlaceChanged={onPlaceChanged}
                  >
                    <input
                      type="text"
                      placeholder={t('create_order.search_places')}
                      aria-label="searchLabel"
                      className="w-full bg-[var(--bg-surface-2)] text-[var(--text-main)] placeholder:text-[var(--text-muted)] p-2 rounded border border-[var(--border-light)] focus:outline-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        // If user starts typing here, selection isn't confirmed yet.
                      }}
                    />
                  </Autocomplete>
                ) : (
                  <p className="text-gray-300">Loading Autocomplete...</p>
                )}
              </div>

              <div className="h-96 w-full">
                {isLoaded ? (
                  <GoogleMap
                    key={mapKey}
                    onLoad={onLoadMap}
                    onClick={handleMapClick}
                    mapContainerStyle={{
                      height: '100%',
                      width: '100%',
                    }}
                    center={defaultCenter}
                    zoom={10}
                    options={{
                      fullscreenControl: false,
                      zoomControl: true,
                      styles: CONSTANTS.GOOGLE_MAP_THEME_STYLES_ALL,
                      disableDefaultUI: true,
                      clickableIcons: false,
                      minZoom: 2,
                    }}
                  >
                    {locationSelectionType === 'origin'
                      ? originLocation && (
                          <Marker
                            key={`origin-${originLocation.lat}-${originLocation.lng}`}
                            position={originLocation}
                          />
                        )
                      : destination && (
                          <Marker
                            key={`destination-${destination.lat}-${destination.lng}`}
                            position={destination}
                          />
                        )}
                  </GoogleMap>
                ) : (
                  <p>Map is loading...</p>
                )}
              </div>
            </DrawerBody>
            <DrawerFooter className="m-auto">
              <Button
                onPress={toggleMapDrawer}
                className="rounded-lg shadow-md font-semibold text-medium transition-colors"
                style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-invert)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
                startContent={
                  <img
                    src="/assets/icons/orders/jobDetails/tick_circle.svg"
                    className="w-5"
                  />
                }
              >
                {t('order_details.confirm')}
              </Button>
            </DrawerFooter>
          </>
        </DrawerContent>
      </Drawer>

      <CustomSnackbar
        open={openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </div>
  );
}
